import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '../supabase.js';
import { followUpsOn, formatDay } from './adminData.js';
import EmailDetail from './EmailDetail.jsx';
import { OUTREACH_REFRESH_EVERY, lastOutreach, loadOutreach } from './outreachData.js';
import OutreachSchedule from './OutreachSchedule.jsx';
import { isNewReply, lastReplyAt, markSeen, onSeenChange, readSeen, startSeen } from './repliesSeen.js';
import { SortableTable } from './SortableTable.jsx';
import { labelOf } from './sorting.js';
import { ListingSwitch, PartnerSwitch, StagePanel, useProviderStages } from './ProviderStages.jsx';
import { emailStatus, formatWhen } from './staffAdmin.js';

const noRows = [];

function NextFollowUp({ row }) {
  if (row.due === 'overdue') return <span className="admin-status is-bad">{`Overdue · ${formatDay(row.nextFollowUp)}`}</span>;
  if (row.due === 'today') return <span className="admin-status is-warn">Today</span>;
  return row.nextFollowUp ? formatDay(row.nextFollowUp) : '–';
}

/** Things in a row that do their own job when clicked, so they must not also open or close the conversation. */
const ownClicks = 'a, button, input, select, textarea, label, details';

/** The whole conversation with a provider, newest first, compact: its subject once, then each email's line and what it says. */
function EmailChain({ row }) {
  const chain = row.chain ?? noRows;
  // The subject is the one the conversation started with.
  const subject = chain[0]?.subject || 'No subject';
  return (
    <div className="admin-email-chain">
      <h3 className="admin-subsection">{subject}</h3>
      {[...chain].reverse().map((email) => (
        <section key={`${email.kind}:${email.id}`} aria-label={email.kind === 'sent' ? `Outreach email of ${formatWhen(email.at)}` : `Reply of ${formatWhen(email.at)}`}>
          <EmailDetail id={email.id} kind={email.kind} compact threadSubject={subject}/>
        </section>
      ))}
    </div>
  );
}

// A provider's place in the order Naima first emailed them, the same in both tables, with a star if they are on the BedMaps directory.
const orderColumn = {
  key: 'order',
  label: 'No.',
  value: (row) => row.sentOrder,
  cell: (row) => (
    <span className="admin-nowrap">
      {row.sentOrder}
      {row.onDirectory && (
        <span className="admin-directory-star" title="On the BedMaps directory">
          <span aria-hidden="true">★</span>
          <span className="visually-hidden"> (on the BedMaps directory)</span>
        </span>
      )}
    </span>
  ),
};

/** The reply a provider becomes a partner from: the day of their first real answer and that email. */
const replyOf = (row) => ({
  repliedOn: row.repliedAt ? String(row.repliedAt).slice(0, 10) : null,
  emailId: row.chain?.find((email) => email.kind === 'received' && email.at === row.repliedAt)?.id ?? null,
});

// Who Naima has emailed and not heard back from: where each email went and what Resend knows of it.
const outreachColumns = [
  orderColumn,
  { key: 'provider', label: 'Provider', value: (row) => row.organisation, cell: (row) => row.organisation },
  { key: 'council', label: 'Council', value: (row) => row.councils, cell: (row) => row.councils || '–' },
  { key: 'emailed', label: 'Emailed', value: (row) => row.emailedAddress, cell: (row) => row.emailedAddress },
  // Only the first email is sent for now, so the step and the next follow-up say nothing until followUpsOn (adminData.js) is true.
  ...(followUpsOn ? [{ key: 'step', label: 'Step', value: (row) => row.sends, first: 'desc', cell: (row) => row.step }] : []),
  { key: 'last', label: 'Last sent', value: (row) => row.lastSentAt, first: 'desc', cell: (row) => formatDay(row.lastSentAt) },
  ...(followUpsOn ? [{ key: 'next', label: 'Next follow-up', value: (row) => row.nextFollowUp, cell: (row) => <NextFollowUp row={row}/> }] : []),
  {
    key: 'delivery',
    label: 'Delivery',
    value: (row) => labelOf(row.status, (value) => emailStatus(value).label),
    cell: (row) => { const { label, tone } = emailStatus(row.status); return <span className={`admin-status is-${tone}`}>{label}</span>; },
  },
];

/**
 * The housing provider outreach emails, read live from Resend, as sections of the Outreach tab: first the providers who replied to
 * naima@ (newest reply first, each opening the whole conversation), then the ones emailed and not heard back from, with what Resend
 * knows of the email. A provider moves from the second to the first as soon as they reply. Each reply has the two switches that take a
 * provider from their reply to partner and on to BedMaps. Then today's scheduled emails and the reserves.
 */
export default function ProviderOutreach() {
  // The last read shows at once (if there is one) while Naima's mailbox is read again.
  const [state, setState] = useState(() => ({ outreach: lastOutreach(), error: '', loading: true }));
  // Trying again after a failure reads the schedule below again too.
  const [scheduleRefresh, setScheduleRefresh] = useState(0);
  // The provider whose conversation is opened under its row ('' when none is).
  const [openId, setOpenId] = useState('');
  // The replies already opened here (null until known). A reply newer than the one opened is new, and its row shows green.
  const [seen, setSeen] = useState(readSeen);
  // Every request takes a number; only the newest one is allowed to change the screen.
  const latest = useRef(0);
  // When the mailbox was last asked for.
  const askedAt = useRef(0);

  const load = useCallback(async () => {
    const ticket = ++latest.current;
    askedAt.current = Date.now();
    setState((current) => ({ ...current, loading: true }));
    try {
      const outreach = await loadOutreach(supabase, { isCurrent: () => ticket === latest.current });
      if (!outreach || ticket !== latest.current) return;
      setState({ outreach, error: '', loading: false });
    } catch (cause) {
      if (ticket !== latest.current) return;
      setState((current) => ({ outreach: current.outreach, error: cause.message || "Could not load the outreach emails. Please try again.", loading: false }));
    }
  }, []);

  useEffect(() => {
    void load();
    return () => { latest.current += 1; };
  }, [load]);

  // Read again (the schedule too) once 10 minutes have passed, checked every minute, and on coming back to the browser tab.
  // Never while the browser tab is in the background.
  useEffect(() => {
    const check = () => {
      if (document.hidden || Date.now() - askedAt.current < OUTREACH_REFRESH_EVERY) return;
      setScheduleRefresh((key) => key + 1);
      void load();
    };
    const timer = window.setInterval(check, 60 * 1000);
    document.addEventListener('visibilitychange', check);
    return () => { window.clearInterval(timer); document.removeEventListener('visibilitychange', check); };
  }, [load]);

  const stages = useProviderStages();
  const { outreach, error, loading } = state;
  const rows = outreach?.rows ?? noRows;
  const replied = useMemo(
    () => rows.filter((row) => row.repliedAt).sort((a, b) => Date.parse(lastReplyAt(b)) - Date.parse(lastReplyAt(a))),
    [rows],
  );
  // The first time, every reply already there counts as read, so only the ones that arrive after it show as new.
  useEffect(() => {
    if (!seen && outreach) setSeen(startSeen(outreach.rows));
  }, [seen, outreach]);
  // Opening a reply here, or in another tab of this browser, is seen at once.
  useEffect(() => onSeenChange(() => setSeen(readSeen())), []);
  const isNew = (row) => isNewReply(row, seen);

  // With follow-ups on, buildOutreach puts the ones due first; without them, the newest email leads.
  const waiting = useMemo(() => {
    const open = rows.filter((row) => !row.repliedAt);
    return followUpsOn ? open : open.sort((a, b) => Date.parse(b.lastSentAt) - Date.parse(a.lastSentAt));
  }, [rows]);

  /** The whole row opens the conversation; the button is how a keyboard or screen reader does the same (its click bubbles to the row). */
  const rowProps = (row) => {
    const open = openId === row.providerId;
    return {
      className: `admin-email-row${open ? ' is-open' : ''}${isNew(row) ? ' is-new' : ''}`,
      onClick: (event) => {
        const own = event.target.closest?.(ownClicks);
        if (own && !own.classList.contains('admin-email-toggle')) return;
        if (!open) setSeen(markSeen(row));
        setOpenId(open ? '' : row.providerId);
      },
    };
  };

  const replyColumns = [
    orderColumn,
    {
      key: 'provider',
      label: 'Provider',
      value: (row) => row.organisation,
      cell: (row) => (
        <span className="admin-nowrap">
          <button type="button" className="admin-email-toggle" aria-expanded={openId === row.providerId} aria-label={`Show the emails with ${row.organisation}`}>
            <span aria-hidden="true">{openId === row.providerId ? '▾' : '▸'}</span>
          </button>
          {row.organisation}
          {isNew(row) && <span className="visually-hidden"> (new reply)</span>}
        </span>
      ),
    },
    { key: 'council', label: 'Council', value: (row) => row.councils, cell: (row) => row.councils || '–' },
    { key: 'address', label: 'Email', value: (row) => row.emailedAddress, cell: (row) => row.emailedAddress },
    { key: 'last', label: 'Last reply', value: lastReplyAt, first: 'desc', cell: (row) => formatWhen(lastReplyAt(row)) },
    ...(stages.ready ? [
      {
        key: 'partner',
        label: 'Partner',
        value: (row) => (stages.partners.get(row.providerId)?.is_partner ? 1 : 0),
        cell: (row) => <PartnerSwitch stages={stages} id={row.providerId} name={row.organisation} reply={replyOf(row)}/>,
      },
      {
        key: 'bedmaps',
        label: 'On BedMaps',
        value: (row) => (stages.listed.has(row.providerId) ? 1 : 0),
        cell: (row) => <ListingSwitch stages={stages} id={row.providerId} name={row.organisation}/>,
      },
    ] : []),
  ];

  return (
    <>
      <h2 className="admin-section">Housing provider emails</h2>
      {error && (
        <p className="mod-error" role="alert">
          {`${error} `}
          <button type="button" className="admin-link" disabled={loading} onClick={() => { setScheduleRefresh((key) => key + 1); void load(); }}>Refresh</button>
        </p>
      )}
      <StagePanel stages={stages}/>
      {!outreach && !error && <p>Loading outreach emails…</p>}
      {outreach && (replied.length === 0
        ? <p className="admin-empty">No replies yet.</p>
        : (
          <SortableTable
            columns={replyColumns}
            rows={replied}
            rowKey={(row) => row.providerId}
            rowProps={rowProps}
            detail={(row) => (openId === row.providerId ? <EmailChain row={row}/> : null)}
          />
        ))}
      {outreach && !outreach.complete && <p className="mod-note">Only Resend's latest 300 sent and 300 received emails were checked, so older outreach emails may be missing.</p>}

      <h2 className="admin-section">Housing provider outreach</h2>
      {outreach && (waiting.length === 0
        ? <p className="admin-empty">{rows.length === 0 ? "No housing providers emailed yet." : 'Everyone emailed has replied.'}</p>
        : <SortableTable columns={outreachColumns} rows={waiting} rowKey={(row) => row.providerId}/>)}

      {/* The plan comes from our own database, so it is shown even when Resend cannot be reached. */}
      <OutreachSchedule refreshKey={scheduleRefresh}/>
    </>
  );
}
